// -----------------------------------------------------------
// FloraIQ - BaseSurveyStagePanel (Aba Etapa 2 - Levantamento Base)
// -----------------------------------------------------------

import { useState } from "react";
import { baseSurveyChecklist, baseSurveys } from "@/mocks/operational";

const tipoLabels: Record<string, string> = {
  drone_rgb: "Drone RGB",
  drone_lidar: "Drone LiDAR",
  laser_scanner: "Laser Scanner",
  ortofoto: "Ortofoto",
  nuvem_fotogrametrica: "Nuvem Fotogramétrica",
  gnss: "GNSS",
  base_cartografica: "Base Cartográfica",
};

export default function BaseSurveyStagePanel() {
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(
    Object.fromEntries(baseSurveyChecklist.map((c) => [c.id, c.concluido]))
  );

  const toggleCheck = (id: string) => {
    setCheckedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const completedCount = Object.values(checkedItems).filter(Boolean).length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Checklist */}
        <div className="lg:col-span-2 space-y-5">
          <div className="rounded-card border border-background-200/70 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-foreground-900">Checklist do Levantamento Base</h4>
              <span className="text-xs text-foreground-500">{completedCount}/{baseSurveyChecklist.length}</span>
            </div>
            <div className="w-full bg-background-100 rounded-full h-1.5 mb-3">
              <div className="bg-accent-500 h-1.5 rounded-full transition-all" style={{ width: `${(completedCount / baseSurveyChecklist.length) * 100}%` }}></div>
            </div>
            <div className="space-y-1">
              {baseSurveyChecklist.map((item) => (
                <button key={item.id} onClick={() => toggleCheck(item.id)} className="w-full flex items-start gap-2.5 p-2.5 rounded-lg text-left transition-colors cursor-pointer hover:bg-background-50/70">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${
                    checkedItems[item.id] ? "bg-green-500 border-green-500 text-white" : "border-background-300 text-transparent hover:border-accent-300"
                  }`}>
                    <i className="ri-check-line text-xs"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs ${checkedItems[item.id] ? "text-foreground-500 line-through" : "text-foreground-800"}`}>{item.descricao}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-foreground-400">{item.responsavel}</span>
                      <span className="text-[10px] text-foreground-300">•</span>
                      <span className="text-[10px] text-foreground-400">{item.prazo}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tabela de levantamentos */}
          <div className="rounded-card border border-background-200/70 p-4">
            <h4 className="text-sm font-bold text-foreground-900 mb-3">Levantamentos Realizados</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/50">
                    <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Nome</th>
                    <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Tipo</th>
                    <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Data</th>
                    <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Área</th>
                    <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Status</th>
                    <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Qualidade</th>
                    <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Pré-ID</th>
                  </tr>
                </thead>
                <tbody>
                  {baseSurveys.map((s) => (
                    <tr key={s.id} className="border-b border-background-100/70 hover:bg-background-50/50">
                      <td className="px-2 py-2 text-xs font-medium text-foreground-800">{s.nome}</td>
                      <td className="px-2 py-2 text-xs text-foreground-600">{tipoLabels[s.tipo] || s.tipo}</td>
                      <td className="px-2 py-2 text-xs text-foreground-600">{s.data}</td>
                      <td className="px-2 py-2 text-xs text-foreground-600">{s.areaCoberta}</td>
                      <td className="px-2 py-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${
                          s.status === "Processado" || s.status === "Disponível" || s.status === "Validado" ? "bg-green-100 text-green-700" :
                          s.status === "Em processamento" ? "bg-accent-100 text-accent-700" :
                          "bg-orange-100 text-orange-700"
                        }`}>{s.status}</span>
                      </td>
                      <td className="px-2 py-2">
                        {s.qualidade > 0 ? (
                          <div className="flex items-center gap-1.5">
                            <div className="w-10 bg-background-100 rounded-full h-1">
                              <div className={`h-1 rounded-full ${s.qualidade >= 90 ? "bg-green-500" : s.qualidade >= 80 ? "bg-accent-500" : "bg-orange-500"}`} style={{ width: `${s.qualidade}%` }}></div>
                            </div>
                            <span className="text-[10px] text-foreground-600">{s.qualidade}%</span>
                          </div>
                        ) : <span className="text-[10px] text-foreground-400">-</span>}
                      </td>
                      <td className="px-2 py-2 text-xs text-foreground-600">{s.arvoresPreIdentificadas > 0 ? s.arvoresPreIdentificadas.toLocaleString("pt-BR") : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Painel lateral */}
        <div className="space-y-4">
          <div className="rounded-card border border-background-200/70 p-4">
            <h4 className="text-sm font-bold text-foreground-900 mb-3">Painel do Levantamento</h4>
            <div className="space-y-2.5">
              {[
                { label: "Ortomosaico", status: "Disponível", icon: "ri-image-line", ok: true },
                { label: "Nuvem de Pontos", status: "Parcial (sem LiDAR)", icon: "ri-stack-line", ok: false },
                { label: "Camadas GIS Carregadas", status: "Concluído", icon: "ri-map-2-line", ok: true },
                { label: "Área Coberta", status: "98,7%", icon: "ri-focus-3-line", ok: true },
                { label: "Qualidade do Levantamento", status: "92%", icon: "ri-check-double-line", ok: true },
                { label: "Pendências Processamento", status: "2 pendências", icon: "ri-alert-line", ok: false },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2.5 p-2 rounded-lg bg-background-50/70">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.ok ? "bg-green-100" : "bg-orange-100"}`}>
                    <i className={`${item.icon} ${item.ok ? "text-green-600" : "text-orange-600"} text-sm`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-700">{item.label}</p>
                    <p className={`text-[10px] font-medium ${item.ok ? "text-green-600" : "text-orange-600"}`}>{item.status}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}